import React from "react";
import LoadingScreen from "../Utils/LoadingScreen";
import { Phone } from "lucide-react";
import DriverDropdown from "../Utils/DriverDropdown";

const DriverTaskTable = ({
  activeTab,
  drivers,
  packerTaskFilters,
  setPackerTaskFilters,
  fetchPackerTasks,
  packerTasksLoading,
  packerTasks,
  getAssignedTaskDriver,
  formatTaskDuration,
  formatAmount,
  getTaskStatusColor,
  formatDateTime,
}) => {
  return (
    <div>
      {activeTab === "Packer Tasks" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-white p-4 shadow-sm">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Driver
              </label>

              <div className="w-[220px]">
                <DriverDropdown
                  drivers={(drivers || []).map((driver) => ({
                    driverId: driver.driverId || driver.driver_code,
                    fullName:
                      driver.fullName ||
                      driver.name ||
                      driver.username ||
                      driver.driverId ||
                      driver.driver_code,
                  }))}
                  value={packerTaskFilters.driverId}
                  onChange={(driverId) =>
                    setPackerTaskFilters((prev) => ({
                      ...prev,
                      driverId,
                    }))
                  }
                  placeholder="All Drivers"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                From Date
              </label>

              <input
                type="date"
                value={packerTaskFilters.fromDate}
                onChange={(e) =>
                  setPackerTaskFilters((prev) => ({
                    ...prev,
                    fromDate: e.target.value,
                  }))
                }
                className="rounded-lg border px-3 py-2 text-xs outline-none focus:border-orange-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                To Date
              </label>

              <input
                type="date"
                value={packerTaskFilters.toDate}
                min={packerTaskFilters.fromDate || undefined}
                onChange={(e) =>
                  setPackerTaskFilters((prev) => ({
                    ...prev,
                    toDate: e.target.value,
                  }))
                }
                className="rounded-lg border px-3 py-2 text-xs outline-none focus:border-orange-500"
              />
            </div>

            <button
              type="button"
              onClick={fetchPackerTasks}
              disabled={packerTasksLoading}
              className="rounded-lg bg-orange-500 px-4 py-2 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {packerTasksLoading ? "Searching..." : "Search Tasks"}
            </button>
          </div>

          {/* Task Table */}
          {packerTasksLoading ? (
            <LoadingScreen />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="max-h-[770px] overflow-x-auto">
                <table className="min-w-[1250px] w-full">
                  <thead className="sticky top-0 z-20 bg-orange-400 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Parcel
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Sender
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Receiver
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Trip
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Fare
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Task Status
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Driver
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                        Created
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 bg-white">
                    {packerTasks.length > 0 ? (
                      packerTasks.map((task, index) => {
                        const parcelId =
                          task?.pk_parcel_id || `parcel-${index}`;

                        const driverAssigned =
                          task?.driver_id !== null &&
                          task?.driver_id !== undefined &&
                          task?.driver_id !== "";

                        const assignedDriver = getAssignedTaskDriver(task);

                        const isActiveForPacker =
                          String(
                            assignedDriver?.is_active_for_packer || "N",
                          ).toUpperCase() === "Y";

                        const packerTeamId =
                          assignedDriver?.packer_team_id ?? 0;

                        return (
                          <tr
                            key={parcelId}
                            className="align-top transition-colors hover:bg-orange-50/40"
                          >
                            {/* Parcel */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <div className="text-sm font-semibold text-gray-900">
                                  #{task?.pk_parcel_id || "-"}
                                </div>

                                <div className="text-xs text-gray-500">
                                  Customer: {task?.customer_id || "-"}
                                </div>

                                <div className="text-xs text-gray-500">
                                  Vehicle: {task?.vehicle_id || "-"}
                                </div>
                              </div>
                            </td>

                            {/* Sender */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {task?.sender_name || "-"}
                                </div>

                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <Phone size={13} className="text-gray-400" />
                                  {task?.sender_number || "-"}
                                </div>
                              </div>
                            </td>

                            {/* Receiver */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {task?.receiver_name || "-"}
                                </div>

                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <Phone size={13} className="text-gray-400" />
                                  {task?.receiver_number || "-"}
                                </div>
                              </div>
                            </td>

                            {/* Trip */}
                            <td className="px-4 py-3">
                              <div className="space-y-1.5 text-xs text-gray-700">
                                <div>
                                  <span className="font-medium">Distance:</span>{" "}
                                  {task?.distance ? `${task.distance} km` : "-"}
                                </div>

                                <div>
                                  <span className="font-medium">Duration:</span>{" "}
                                  {formatTaskDuration(task?.duration)}
                                </div>

                                <div
                                  className="max-w-[220px] truncate text-[11px] text-gray-500"
                                  title={`Pickup: ${task?.pickup_latitude || "-"}, ${
                                    task?.pickup_longitude || "-"
                                  }`}
                                >
                                  Pickup: {task?.pickup_latitude || "-"},{" "}
                                  {task?.pickup_longitude || "-"}
                                </div>

                                <div
                                  className="max-w-[220px] truncate text-[11px] text-gray-500"
                                  title={`Drop: ${task?.dropoff_latitude || "-"}, ${
                                    task?.dropoff_longtidue ||
                                    task?.dropoff_longitude ||
                                    "-"
                                  }`}
                                >
                                  Drop: {task?.dropoff_latitude || "-"},{" "}
                                  {task?.dropoff_longtidue ||
                                    task?.dropoff_longitude ||
                                    "-"}
                                </div>
                              </div>
                            </td>

                            {/* Fare */}
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-green-700">
                                {formatAmount(task?.fare)}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <div className="space-y-2">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getTaskStatusColor(
                                    task?.status,
                                  )}`}
                                >
                                  {task?.status || "-"}
                                </span>

                                {task?.cancel_reason && (
                                  <div
                                    className="max-w-[180px] text-xs text-red-600"
                                    title={task.cancel_reason}
                                  >
                                    {task.cancel_reason}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Driver */}
                            {/* Driver */}
                            <td className="px-4 py-3">
                              <div className="space-y-1.5">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                    driverAssigned
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  {task?.driver_status || "unassigned"}
                                </span>

                                <div className="text-xs text-gray-600">
                                  Driver ID: {task?.driver_id || "Not assigned"}
                                </div>

                                {assignedDriver && (
                                  <>
                                    <div className="text-xs font-medium text-gray-800">
                                      {assignedDriver?.fullName ||
                                        assignedDriver?.name ||
                                        assignedDriver?.username ||
                                        assignedDriver?.driverId ||
                                        "-"}
                                    </div>

                                    <span
                                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        isActiveForPacker
                                          ? "bg-orange-100 text-orange-700"
                                          : "bg-gray-100 text-gray-600"
                                      }`}
                                    >
                                      {isActiveForPacker
                                        ? "Active Packer"
                                        : "Not Packer"}
                                    </span>

                                    <div className="text-[11px] text-gray-500">
                                      Packer Team ID:{" "}
                                      {packerTeamId > 0
                                        ? packerTeamId
                                        : "Not assigned"}
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>

                            {/* Created */}
                            <td className="px-4 py-3">
                              <div className="space-y-1 text-xs text-gray-600">
                                <div>{formatDateTime(task?.created_on)}</div>

                                <div>
                                  Pickup:{" "}
                                  {task?.pickup_time
                                    ? formatDateTime(task.pickup_time)
                                    : "-"}
                                </div>

                                <div>
                                  Drop:{" "}
                                  {task?.dropoff_time
                                    ? formatDateTime(task.dropoff_time)
                                    : "-"}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          No packer tasks found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriverTaskTable;
